import { UsersIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';

import { Bar, Glow, Label } from './OpsOnDutyBar.styles';
import OpsOnDutyEntries from './OpsOnDutyEntries';
import { useOpsOnDuty } from './useOpsOnDuty';
import { barState } from './viewModel';

import { trackOpsBarViewed } from '@/util/vexo';
import type { OpsBarSurface } from '@/util/vexo';

type Props = {
  // Which dashboard mounts the bar: its analytics dimension.
  surface: OpsBarSurface;
  /** The page has no side padding on phones. */
  flush?: boolean;
};

// GRAVY-10878: "Ops on duty today", above the page title on both dashboards
// (the Page `banner` slot). Every role sees it while someone is on duty;
// graphy decides what each may read.
const OpsOnDutyBar = ({ surface, flush = false }: Props) => {
  const { data, isError } = useOpsOnDuty();
  // react-query keeps the last good data after a failed refresh; showing it
  // would keep an ended shift green, with its number, until a reload.
  const state = barState(isError ? undefined : data);

  // One view per dashboard visit, in the state it first showed; the 30 s
  // poll does not count again.
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current || state.kind === 'hidden') return;
    viewed.current = true;
    trackOpsBarViewed({
      surface,
      state: state.kind,
      entries_count: state.entries.length,
      on_duty_count: state.entries.filter((e) => e.on_duty).length,
    });
  });

  if (state.kind === 'hidden') return null;

  return (
    <Bar
      aria-labelledby="opsOnDutyTitle"
      data-testid="opsOnDutyBar"
      data-page-banner=""
      tabIndex={-1}
      flush={flush}
    >
      <Glow aria-hidden="true" />
      <Label id="opsOnDutyTitle">
        <UsersIcon aria-hidden="true" />
        Ops on duty today
      </Label>
      <OpsOnDutyEntries
        entries={state.entries}
        surface={surface}
        timeZone={state.timeZone}
      />
    </Bar>
  );
};

export default OpsOnDutyBar;
